<template>
  <NuxtLayout
    name="generic"
    header="standardDark"
    footer="standard"
  >
    <template v-if="data">
      <div
        :class="{
          'hero-section relative mt-70px min-h-200px bg-cover bg-no-repeat': true,
          'md:mt-24 md:min-h-72 lg:mt-120px lg:min-h-[340px]': true,
        }"
        :style="{
          backgroundImage: `url(${getEventHeroBackgroundImage()})`,
        }"
      >
        <PtcPicture
          v-if="data.illustration.asset"
          provider="sanity"
          :src="data.illustration.asset?._ref"
          alt="Illustration"
          :loading="'eager'"
          :preload="true"
          :img-attrs="{
            class: {
              'lg:block hidden absolute right-100px h-300px top-1/2 -translate-y-1/2': true,
            },
          }"
        />
      </div>
      <div
        :class="{
          'event-wrapper container relative mx-14 -mt-10 mb-120px gap-6': true,
          'md:-mt-12 lg:-mt-60px': true,
          [eventThemeClass]: true,
        }"
      >
        <!-- back to Events Button -->
        <BackToAllEvents
          :class="{
            'absolute -top-12 left-3': true,
          }"
        />
        <!-- Left Column: Main Content -->
        <div
          :class="{
            'event-main w-full rounded-2xl bg-www-color-white px-5 pb-0 pt-10 shadow-[0px_20px_54px_0px_rgba(13,46,72,0.14)] md:rounded-b-none': true,
            'md:px-30px md:pb-0 md:pt-12 lg:px-11 lg:pt-14': true,
          }"
        >
          <div>
            <!-- Title -->
            <h1
              :class="{
                'mb-6 text-3xl font-extrabold leading-none tracking-[-0.29px] text-www-color-neutral-dark': true,
                'md:text-4xl md:leading-none md:tracking-[-0.36px] lg:text-42px lg:leading-[44px] lg:tracking-[-0.42px]': true,
              }"
            >
              {{ data.title }}
            </h1>

            <!-- Event Meta Card -->
            <div
              :class="{
                'mb-5 rounded-xl border border-solid px-6 py-5 md:mb-34px lg:mb-12': true,
                'border-www-color-accent-soft bg-www-color-accent-almost-white': true,
              }"
            >
              <!-- WHEN -->
              <div class="flex flex-wrap justify-between gap-2">
                <div
                  v-if="data.startDateTime && data.endDateTime"
                  :class="{ 'flex flex-col gap-[6px]': true }"
                >
                  <span
                    :class="{
                      'text-11px font-extrabold uppercase tracking-[1.54px] text-www-color-accent': true,
                    }"
                  >
                    WHEN
                  </span>
                  <div :class="{ 'flex items-start gap-[9px] lg:items-center': true }">
                    <CalendarIcon :class="{ 'mt-1 w-[18px] shrink-0 md:mt-0': true }" />
                    <span
                      :class="{
                        'flex flex-col text-15px font-bold text-www-color-neutral-dark': true,
                      }"
                    >
                      <span
                        >From:
                        {{
                          formatLocalDateWithZone(
                            data.startDateTime.utc,
                            'DD MMM YYYY, HH:mm z ',
                            selectedTimeZone
                          )
                        }}</span
                      >
                      <span
                        >To:
                        {{
                          formatLocalDateWithZone(
                            data.endDateTime.utc,

                            'DD MMM YYYY, HH:mm z',
                            selectedTimeZone
                          )
                        }}</span
                      >
                    </span>
                  </div>
                </div>
                <TimezoneDropdown v-model="selectedTimeZone" />
              </div>
              <div
                v-if="data.location"
                :class="{ 'my-4 h-px w-full bg-www-color-neutral-very-light': true }"
              ></div>
              <!-- WHERE -->
              <div
                v-if="data.location"
                :class="{ 'flex flex-col gap-[6px]': true }"
              >
                <span
                  :class="{
                    'text-11px font-extrabold uppercase tracking-[1.54px] text-www-color-accent': true,
                  }"
                >
                  WHERE
                </span>
                <div :class="{ 'flex items-start gap-[9px] lg:items-center': true }">
                  <DesktopIcon v-if="data.eventFormat === 'virtual'" />
                  <LocationIcon v-else />
                  <span
                    :class="{
                      'text-15px font-bold capitalize text-www-color-neutral-dark': true,
                    }"
                  >
                    {{ data.location }}
                  </span>
                </div>
              </div>
              <div
                v-if="data.featuring"
                :class="{ 'my-4 h-px w-full bg-www-color-neutral-very-light': true }"
              ></div>
              <!-- SPEAKERS -->
              <div
                v-if="data.featuring"
                :class="{ 'flex flex-col gap-[6px]': true }"
              >
                <span
                  :class="{
                    'text-11px font-extrabold uppercase tracking-[1.54px] text-www-color-accent': true,
                  }"
                >
                  FEATURING
                </span>
                <div :class="{ 'flex items-start gap-[9px] lg:items-center': true }">
                  <UsersIcon />
                  <span
                    :class="{
                      'text-15px font-bold text-www-color-neutral-dark': true,
                    }"
                  >
                    {{ data.featuring }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Right Column: Aside (Form + Info) -->
        <div
          v-if="
            data?.sectionBuilderForm?.form ||
            data?.virtualLocation?.label ||
            data?.mappedLocation?.location
          "
          :class="{ 'event-aside w-full': true }"
        >
          <div :class="{ 'flex min-w-0 flex-col gap-[22px]': true }">
            <!-- Registration Form Card -->
            <div
              v-if="data?.sectionBuilderForm?.form"
              :class="{
                'rounded-14px border border-solid bg-www-color-white pb-6': true,
                'border-www-color-primary-lighter shadow-[0_12px_34px_rgba(64,170,29,0.16)]': true,
              }"
            >
              <!-- Form Header -->
              <div
                :class="{
                  'flex items-center justify-end px-6 pt-5': true,
                }"
              >
                <span
                  :class="{
                    'rounded-md px-3 py-[6px] font-ptc-Rajdhani text-sm font-bold uppercase tracking-[0.45px] lg:text-15px': true,
                    'bg-www-color-accent-soft text-www-color-accent-light': true,
                  }"
                >
                  {{ data.cardTime }}
                </span>
              </div>

              <SectionBuilderForm
                v-if="data?.sectionBuilderForm"
                v-bind="data?.sectionBuilderForm"
                :class="{
                  'form-wrapper': true,
                }"
              />
            </div>

            <!-- Join Online Info Card -->
            <VirtualLocation
              v-if="data?.virtualLocation?.label"
              v-bind="data?.virtualLocation"
            />
            <ExtraDetails
              v-if="data?.extraDetails?.label"
              v-bind="data?.extraDetails"
            />
            <!-- Mapped Location Info Card -->
            <MappedLocation
              v-if="data?.mappedLocation?.location"
              v-bind="data?.mappedLocation"
            />
          </div>
        </div>
        <!-- Event Content Section -->
        <SectionBuilder
          v-if="data.content"
          v-bind="data.content"
          :class="{
            'event-content rounded-2xl bg-www-color-white px-5 pb-30px pt-5 text-www-color-neutral-medium-dark shadow-[0px_20px_54px_0px_rgba(13,46,72,0.14)]': true,
            'md:-mt-7 md:rounded-t-none md:p-0 md:px-30px md:pb-34px md:pt-0 md:shadow-[0px_20px_54px_-24px_rgba(13,46,72,0.14)] lg:px-11 lg:pb-11 lg:pt-0': true,
          }"
        />
      </div>
    </template>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { queryEvent } from '~/queries/pages/PageEvent.query';
import type { QueryEventResult } from '~/sanity/types';
import SectionBuilder from '~/components/Atmos/SectionBuilder/SectionBuilder.vue';
import SectionBuilderForm from '~/components/Atmos/Form/SectionBuilderForm.vue';
import VirtualLocation from '~/components/Molecules/EventsComponents/VirtualLocation/VirtualLocation.vue';
import ExtraDetails from '~/components/Molecules/EventsComponents/ExtraDetails/ExtraDetails.vue';
import CalendarIcon from '~/components/Molecules/EventsComponents/CalendarIcon/CalendarIcon.vue';
import DesktopIcon from '~/components/Molecules/EventsComponents/DesktopIcon/DesktopIcon.vue';
import UsersIcon from '~/components/Molecules/EventsComponents/UsersIcon/UsersIcon.vue';
import MappedLocation from '~/components/Molecules/EventsComponents/MappedLocation/MappedLocation.vue';
import PtcPicture from '~/components/Atmos/PtcPicture/PtcPicture.vue';
import LocationIcon from '~/components/Molecules/EventsComponents/LocationIcon/LocationIcon.vue';
import TimezoneDropdown from '~/components/Molecules/EventsComponents/TimezoneDropdown/TimezoneDropdown.vue';
import BackToAllEvents from '~/components/Molecules/EventsComponents/BackToAllEvents/BackToAllEvents.vue';

const { path: currentPathname } = useRoute();
const { data } = await useWwwSanityQueryNonNull<QueryEventResult>(queryEvent, {
  currentPathname,
});

const { eventThemeClass, getEventHeroBackgroundImage } = useEventTheme({
  theme: () => data.value?.theme ?? null,
});

const fallbackTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const initialTimeZone =
  data.value?.startDateTime?.timezone ?? data.value?.endDateTime?.timezone ?? fallbackTimeZone;

const selectedTimeZone = ref<string>(initialTimeZone);

usePageHead({
  _id: data.value?._id,
  description: data.value?.ogDescription || data.value?.description,
  ogImage: data.value?.ogImage?.asset || data.value?.cardImage?.asset,
  title: data.value?.htmlTitle || data.value?.title,
  excludeFromExternalSearch: data.value?.excludeFromExternalSearch,
  twitterCard: 'summary',
  structuredData: [
    {
      schemaType: 'Event',
      title: data.value?.htmlTitle || data.value?.title,
      description: data.value?.ogDescription || data.value?.description,
      startDate: data.value?.startDateTime?.utc,
      endDate: data.value?.endDateTime?.utc,
      locationName: data.value?.location,
      organizerName: STRUCTURED_DATA_DEFAULTS.organization.name,
      organizerUrl: STRUCTURED_DATA_DEFAULTS.organization.url,
      imageRef: data.value?.ogImage?.asset?._ref || data.value?.cardImage?.asset?._ref,
      enabled: true,
    },
  ],
});
</script>
<style lang="scss">
.event-wrapper {
  @media (min-width: 768px) and (max-width: 1024px) {
    max-width: 900px !important;
  }
  display: grid;
  grid-template-areas:
    'main'
    'aside'
    'content';

  .event-main {
    grid-area: main;
  }

  .event-aside {
    grid-area: aside;
  }

  .event-content {
    grid-area: content;

    @media (min-width: 1024px) {
      .testimonial-images {
        .img-background {
          width: 240px !important;
        }
      }
    }

    @media (min-width: 1280px) {
      .testimonial-images {
        .img-background {
          width: 270px !important;
        }
      }
    }
  }
  @media (min-width: 768px) {
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    grid-template-rows: auto 1fr;
    grid-template-areas:
      'main aside'
      'content aside';

    // when aside has no rendered children, left section take full width
    &:not(:has(.event-aside > * > *)) {
      grid-template-columns: 1fr;
      grid-template-rows: auto auto;
      grid-template-areas:
        'main'
        'content';
    }
  }
  .faq-accordion-component {
    > .bg-wrapper {
      > .padding-wrapper {
        > .container {
          padding: 0 !important;
        }
      }
    }

    .accordion {
      > .text-one-column {
        margin-bottom: 18px !important;

        .content {
          font-size: 13px !important;
          font-weight: 800 !important;
          line-height: 1 !important;
          letter-spacing: 2.08px !important;
          text-transform: uppercase !important;
          padding: 0 12px !important;
          color: var(--www-color-accent) !important;

          &::before {
            content: '';
            position: relative;
            top: -0.25rem;
            left: 0;
            margin-right: 10px;
            display: inline-block;
            height: 2px;
            width: 25px;
            background: var(--www-color-accent);
          }
        }
      }
    }
  }
  .form-wrapper {
    .form-title {
      color: #21303a !important;
      font-size: 21px !important;
      font-weight: 800 !important;
      text-transform: capitalize !important;
    }
    #hekate-form-fort-container .thank-you-message {
      color: #21303a !important;
    }
  }
}
</style>
